import { Link, useLocation } from 'react-router-dom';
import { ListChecks } from 'lucide-react';
import type { MeResponse } from '@ucp/web/lib';
import { NavMenuIconLabel } from '../shell/NavMenuIconLabel';
import { cn } from '../ui/cn';
import { WEEKLY_REPORT_BASE } from './opsPresentation';
import { showMyActions } from './opsRoles';

export const MY_ACTIONS_PATH = `${WEEKLY_REPORT_BASE}/my-actions`;

/**
 * The ADR-014 A16 "My actions" link: a text link in the header beside the account menu
 * (from `md`), and a row above the module list in the mobile drawer. Shown only with
 * `opsIssuesEnabled` and a role carrying `ops_action_own`. No count badge in Increment 1.
 */
export function MyActionsLink({
  user,
  variant,
  onNavigate,
  drawerLinkClassName,
}: {
  user: MeResponse;
  variant: 'header' | 'drawer';
  onNavigate?: () => void;
  drawerLinkClassName?: (active: boolean) => string;
}) {
  const { pathname } = useLocation();
  if (!showMyActions(user)) return null;
  const active = pathname === MY_ACTIONS_PATH || pathname.startsWith(`${MY_ACTIONS_PATH}/`);

  if (variant === 'drawer') {
    return (
      <Link
        to={MY_ACTIONS_PATH}
        aria-current={active ? 'page' : undefined}
        onClick={onNavigate}
        className={drawerLinkClassName?.(active)}
      >
        <NavMenuIconLabel icon={ListChecks} active={active}>
          My actions
        </NavMenuIconLabel>
      </Link>
    );
  }

  return (
    <Link
      to={MY_ACTIONS_PATH}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'hidden min-h-11 items-center rounded-md px-2 text-sm font-medium md:inline-flex',
        'underline-offset-2 hover:underline',
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
        active ? 'text-primary underline' : 'text-text-primary',
      )}
    >
      My actions
    </Link>
  );
}
